import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { MapPin, Phone, Mail, Globe, ChevronRight, Clock, Search, Plus, Edit, Trash2, X } from 'lucide-react';
import ClassCard from '../components/ClassCard';
import AnnouncementCommentSection from '../components/AnnouncementCommentSection';
import RichTextEditor from '../components/RichTextEditor';
import InstructorCard from '../components/InstructorCard';
import InstructorDetailsModal from '../components/InstructorDetailsModal';
import MainTabsNav from '../components/MainTabsNav';
import { useAuthStore } from '../stores/authStore';

interface ProviderDetail {
  id: string;
  email: string;
  company_name: string | null;
  ceo_name: string | null;
  avatar_url: string | null;
  banner_image_url: string | null;
  premium_started_at: string | null;
  description: string | null;
  phone: string | null;
  website: string | null;
  address: string | null;
  operating_hours: string | null;
}

interface ClassItem {
  id: string;
  title: string;
  providerName: string;
  thumbnailUrl: string | null;
  price: number;
  discountPrice: number;
  startDate: string;
  endDate: string;
  location: string;
  locationType: 'ONLINE' | 'OFFLINE' | 'HYBRID';
  classType?: 'CERTIFICATE' | 'COMPLETION' | 'WORKSHOP' | 'OTHER';
  status: 'RECRUITING' | 'IN_PROGRESS' | 'COMPLETED';
  capacity: number;
  currentCount: number;
  instructors?: {
    id: string;
    name: string;
    description: string;
    image_url: string | null;
  }[];
}

interface Announcement {
  id: string;
  provider_id: string;
  title: string;
  content: string;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
  comment_count?: number;
}

interface Instructor {
  id: string;
  provider_id: string;
  name: string;
  description: string;
  image_url: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export default function ProviderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [provider, setProvider] = useState<ProviderDetail | null>(null);
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [classesLoading, setClassesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [classTypeFilter, setClassTypeFilter] = useState<'ALL' | 'CERTIFICATE' | 'COMPLETION' | 'WORKSHOP' | 'OTHER'>('ALL');
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);

  // 공지사항 작성 폼
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementContent, setAnnouncementContent] = useState('');
  const [announcementPinned, setAnnouncementPinned] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 선택된 공지사항 (상세 보기)
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);

  // 페이지네이션 (공지사항)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // 등록된 수업 — 모집중/모집 완료 탭 + 6개씩(2열 x 3행) 페이지 넘김
  const [classStatusTab, setClassStatusTab] = useState<'RECRUITING' | 'CLOSED'>('RECRUITING');
  const [classPage, setClassPage] = useState(1);
  const CLASSES_PER_PAGE = 6;

  // 강사진
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [instructorsLoading, setInstructorsLoading] = useState(true);
  const [selectedInstructor, setSelectedInstructor] = useState<Instructor | null>(null);
  const [showInstructorModal, setShowInstructorModal] = useState(false);

  // 현재 로그인한 사용자가 이 업체의 소유자인지 확인
  const isOwner = user?.id === id && user?.is_premium;

  // 공지사항 선택 핸들러 (토글)
  const handleSelectAnnouncement = (announcement: Announcement) => {
    if (selectedAnnouncement?.id === announcement.id) {
      setSelectedAnnouncement(null);
    } else {
      setSelectedAnnouncement(announcement);
    }
  };

  // 페이지네이션된 공지사항 목록
  const totalPages = Math.ceil(announcements.length / itemsPerPage);
  const paginatedAnnouncements = announcements.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // 페이지 진입(혹은 다른 기관으로 이동) 시 상단으로 스크롤 — 긴 페이지에서 넘어와도 최상단부터 보이게
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [id]);

  // 수업 목록의 탭·검색·종류가 바뀌면 첫 페이지로.
  // ⚠️ 훅은 아래 loading/error early return보다 위에 있어야 한다(조건부 실행 금지)
  useEffect(() => {
    setClassPage(1);
  }, [classStatusTab, searchQuery, classTypeFilter]);

  // 업체 정보 로드
  useEffect(() => {
    const fetchProvider = async () => {
      if (!id) {
        setError('업체 ID가 없습니다.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/v1/providers/${id}`, {
          credentials: 'include',
        });

        if (!response.ok) {
          if (response.status === 404) {
            setError('업체를 찾을 수 없습니다.');
          } else {
            setError('업체 정보를 불러오는데 실패했습니다.');
          }
          setLoading(false);
          return;
        }

        const data = await response.json();
        setProvider(data.provider);
      } catch (err) {
        console.error('Failed to fetch provider:', err);
        setError('업체 정보를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchProvider();
  }, [id]);

  // 업체 수업 목록 로드
  useEffect(() => {
    const fetchClasses = async () => {
      if (!id) return;

      try {
        setClassesLoading(true);
        const response = await fetch(`/api/v1/providers/${id}/classes`, {
          credentials: 'include',
        });

        if (!response.ok) {
          console.error('Failed to fetch classes');
          setClasses([]);
          return;
        }

        const data = await response.json();

        // API 응답을 ClassItem 형식으로 변환
        const formattedClasses: ClassItem[] = (data.classes || []).map((cls: any) => ({
          id: cls.id,
          title: cls.title,
          providerName: provider?.company_name || '업체명 없음',
          thumbnailUrl: cls.thumbnail_url,
          price: cls.price,
          discountPrice: cls.discount_price,
          startDate: cls.start_date,
          endDate: cls.end_date,
          location: cls.location_type === 'ONLINE' ? '온라인' : cls.location_address || '위치 미정',
          locationType: cls.location_type,
          classType: cls.class_type,
          status: cls.status,
          capacity: cls.capacity,
          currentCount: cls.current_count,
          closingSoon: cls.closing_soon,
          isPremium: provider?.is_premium || false,
          instructors: cls.instructors,
        }));

        setClasses(formattedClasses);
      } catch (err) {
        console.error('Failed to fetch classes:', err);
        setClasses([]);
      } finally {
        setClassesLoading(false);
      }
    };

    if (provider) {
      fetchClasses();
    }
  }, [id, provider]);

  // 업체 공지사항 로드
  useEffect(() => {
    const fetchAnnouncements = async () => {
      if (!id) return;

      try {
        setAnnouncementsLoading(true);
        const response = await fetch(`/api/v1/providers/${id}/announcements`, {
          credentials: 'include',
        });

        if (!response.ok) {
          console.error('Failed to fetch announcements');
          setAnnouncements([]);
          return;
        }

        const data = await response.json();
        setAnnouncements(data.announcements || []);
      } catch (err) {
        console.error('Failed to fetch announcements:', err);
        setAnnouncements([]);
      } finally {
        setAnnouncementsLoading(false);
      }
    };

    if (provider) {
      fetchAnnouncements();
    }
  }, [id, provider]);

  // 강사진 로드
  useEffect(() => {
    const fetchInstructors = async () => {
      if (!id) return;

      try {
        setInstructorsLoading(true);
        const response = await fetch(`/api/v1/providers/${id}/instructors`, {
          credentials: 'include',
        });

        if (!response.ok) {
          console.error('Failed to fetch instructors');
          setInstructors([]);
          return;
        }

        const data = await response.json();
        setInstructors(data.instructors || []);
      } catch (err) {
        console.error('Failed to fetch instructors:', err);
        setInstructors([]);
      } finally {
        setInstructorsLoading(false);
      }
    };

    if (provider) {
      fetchInstructors();
    }
  }, [id, provider]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{error || '업체를 찾을 수 없습니다.'}</h2>
          <button
            onClick={() => navigate('/')}
            className="text-primary hover:underline"
          >
            홈으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const defaultBanner = 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=800&h=400&fit=crop';

  // 검색 + 종류 필터링 (상태 탭 적용 전)
  const searchedClasses = classes.filter((cls) => {
    const matchesSearch = cls.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = classTypeFilter === 'ALL' || cls.classType === classTypeFilter;
    return matchesSearch && matchesType;
  });

  // 모집중 / 모집 완료(진행중·종료) 분리 — status는 서버가 날짜 기준으로 계산해 내려준다
  const recruitingClasses = searchedClasses.filter((cls) => cls.status === 'RECRUITING');
  const closedClasses = searchedClasses.filter((cls) => cls.status !== 'RECRUITING');
  const filteredClasses = classStatusTab === 'RECRUITING' ? recruitingClasses : closedClasses;

  const classTotalPages = Math.max(1, Math.ceil(filteredClasses.length / CLASSES_PER_PAGE));
  const paginatedClasses = filteredClasses.slice(
    (classPage - 1) * CLASSES_PER_PAGE,
    classPage * CLASSES_PER_PAGE
  );

  // 섹션 바로가기 — 고정 헤더(h-16) + 탭바 높이만큼은 각 섹션의 scroll-mt-* 로 보정
  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // 공지사항 CRUD 핸들러
  const handleSaveAnnouncement = async () => {
    if (!announcementTitle.trim() || !announcementContent.trim()) {
      setMessage({ type: 'error', text: '제목과 내용을 입력해주세요.' });
      return;
    }

    try {
      const url = editingAnnouncement
        ? `/api/v1/announcements/${editingAnnouncement.id}`
        : '/api/v1/announcements';

      const method = editingAnnouncement ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: announcementTitle,
          content: announcementContent,
          is_pinned: announcementPinned,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save announcement');
      }

      // 목록 새로고침
      const listResponse = await fetch(`/api/v1/providers/${id}/announcements`, {
        credentials: 'include',
      });
      const data = await listResponse.json();
      setAnnouncements(data.announcements || []);

      // 폼 초기화
      setShowAnnouncementForm(false);
      setEditingAnnouncement(null);
      setAnnouncementTitle('');
      setAnnouncementContent('');
      setAnnouncementPinned(false);
      setMessage({ type: 'success', text: editingAnnouncement ? '공지사항이 수정되었습니다.' : '공지사항이 등록되었습니다.' });

      // 3초 후 메시지 자동 제거
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Failed to save announcement:', error);
      setMessage({ type: 'error', text: '공지사항 저장에 실패했습니다.' });
    }
  };

  const handleDeleteAnnouncement = async (announcementId: string) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    try {
      const response = await fetch(`/api/v1/announcements/${announcementId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete announcement');
      }

      setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
      setMessage({ type: 'success', text: '공지사항이 삭제되었습니다.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Failed to delete announcement:', error);
      setMessage({ type: 'error', text: '공지사항 삭제에 실패했습니다.' });
    }
  };

  const handleEditAnnouncement = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setAnnouncementTitle(announcement.title);
    setAnnouncementContent(announcement.content);
    setAnnouncementPinned(announcement.is_pinned);
    setShowAnnouncementForm(true);
    // 폼으로 스크롤
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 강사 카드 클릭 핸들러
  const handleInstructorClick = (instructor: Instructor) => {
    setSelectedInstructor(instructor);
    setShowInstructorModal(true);
  };

  // 수업 카드의 강사 이름 클릭 핸들러
  const handleClassInstructorClick = (instructorId: string) => {
    // 강사 목록에서 찾거나 API에서 가져오기
    const instructor = instructors.find(i => i.id === instructorId);
    if (instructor) {
      setSelectedInstructor(instructor);
      setShowInstructorModal(true);
    } else {
      // 강사 목록에 없는 경우 API로 가져오기
      fetch(`/api/v1/instructors/${instructorId}`, {
        credentials: 'include',
      })
        .then(res => res.json())
        .then(data => {
          setSelectedInstructor(data.instructor);
          setShowInstructorModal(true);
        })
        .catch(err => {
          console.error('Failed to fetch instructor:', err);
        });
    }
  };

  return (
    <div className="bg-gray-50">
      {/* 메인 탭 네비게이션 (스크롤 시 고정) — 메인 화면 탭들로 이동 */}
      <MainTabsNav />

      {/* Provider Header Banner */}
      <div className="px-6 sm:px-10 lg:px-16">
        <div className="relative rounded-t-2xl overflow-hidden">
          {/* 그라데이션 배경 (왼쪽 Navy → 오른쪽 투명) */}
          <div
            className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent"
            style={{ zIndex: 0 }}
          />

          <div className="relative h-48 flex items-end">
            {/* 텍스트 */}
            <div className="px-8 pb-6 z-10">
              <h1 className="text-4xl font-bold text-white mb-2">{provider.company_name || '업체명 없음'}</h1>
              {provider.address && (
                <p className="text-white/90 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {provider.address}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 sm:px-10 lg:px-16 py-3">
          <nav className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-primary">홈</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900">{provider.company_name || '업체명 없음'}</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 sm:px-10 lg:px-16 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Provider Info - Sidebar */}
          <div className="lg:col-span-1">
            {/* 배너 이미지 - sticky 바깥에 배치하여 zoom 시 정상 스케일 */}
            {provider.banner_image_url && (
              <img
                src={provider.banner_image_url}
                alt={provider.company_name || '업체'}
                className="w-full h-auto rounded-t-2xl block"
              />
            )}
            <div className={`bg-white shadow-soft p-6 sticky top-24 ${provider.banner_image_url ? 'rounded-b-2xl' : 'rounded-2xl'}`}>

              {/* 업체명 + 주소 + 연락처 (업체 소개는 그리드 바깥 단독 섹션으로 분리) */}
              <div className="mb-2">
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  {provider.company_name || '업체명 없음'}
                </h2>
                {provider.address && (
                  <p className="text-sm text-gray-600 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                    {provider.address}
                  </p>
                )}
              </div>

              {/* Contact Info — 주소 바로 아래 */}
              <div className="space-y-3 pt-3">
                {provider.phone && (
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{provider.phone}</span>
                  </div>
                )}
                {provider.email && (
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{provider.email}</span>
                  </div>
                )}
                {provider.website && (
                  <div className="flex items-center gap-3 text-sm">
                    <Globe className="w-4 h-4 text-gray-400" />
                    <a
                      href={provider.website.startsWith('http') ? provider.website : `https://${provider.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {provider.website}
                    </a>
                  </div>
                )}
              </div>

              {/* Operating Hours */}
              {provider.operating_hours && (
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                    <Clock className="w-4 h-4" />
                    <p>운영 시간</p>
                  </div>
                  <p className="text-sm font-medium whitespace-pre-line">{provider.operating_hours}</p>
                </div>
              )}
            </div>
          </div>

          {/* Classes - Main */}
          <div className="lg:col-span-2">
          {/* 섹션 바로가기 서브탭 — 클릭 시 해당 위치로 스크롤 */}
          <div className="flex items-center gap-1.5 flex-wrap mb-6 pb-2 border-b border-gray-200">
            {[
              ...(announcementsLoading || announcements.length > 0 || isOwner
                ? [{ id: 'provider-announcements', label: '공지사항' }]
                : []),
              { id: 'provider-classes', label: '등록된 수업' },
              { id: 'provider-about', label: '업체 소개' },
              ...(instructorsLoading || instructors.length > 0
                ? [{ id: 'provider-instructors', label: '강사진' }]
                : []),
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => scrollToSection(tab.id)}
                className="px-3.5 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-accent-50 hover:text-accent-600 transition-colors"
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* 공지사항 */}
          {(announcementsLoading || announcements.length > 0 || isOwner) && (
            <div id="provider-announcements" className="mb-8 scroll-mt-32">
              {/* 메시지 */}
              {message && (
                <div
                  className={`mb-4 p-3 rounded-lg ${
                    message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}
                >
                  {message.text}
                </div>
              )}

              {/* 공지사항 작성/수정 폼 */}
              {showAnnouncementForm && isOwner && (
                <div className="mb-6 bg-white rounded-2xl shadow-soft p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    {editingAnnouncement ? '공지사항 수정' : '새 공지사항 작성'}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        제목 *
                      </label>
                      <input
                        type="text"
                        value={announcementTitle}
                        onChange={(e) => setAnnouncementTitle(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                        placeholder="공지사항 제목을 입력하세요"
                        maxLength={200}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        내용 *
                      </label>
                      <RichTextEditor
                        content={announcementContent}
                        onChange={setAnnouncementContent}
                        placeholder="공지사항 내용을 입력하세요"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="pinned"
                        checked={announcementPinned}
                        onChange={(e) => setAnnouncementPinned(e.target.checked)}
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <label htmlFor="pinned" className="ml-2 text-sm text-gray-700">
                        상단 고정
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveAnnouncement}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm"
                      >
                        {editingAnnouncement ? '수정' : '등록'}
                      </button>
                      <button
                        onClick={() => {
                          setShowAnnouncementForm(false);
                          setEditingAnnouncement(null);
                          setAnnouncementTitle('');
                          setAnnouncementContent('');
                          setAnnouncementPinned(false);
                        }}
                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors text-sm"
                      >
                        취소
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* 공지사항 게시판 */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    공지사항
                  </h2>
                  {isOwner && !showAnnouncementForm && (
                    <button
                      onClick={() => {
                        setShowAnnouncementForm(true);
                        setEditingAnnouncement(null);
                        setAnnouncementTitle('');
                        setAnnouncementContent('');
                        setAnnouncementPinned(false);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      새 글 등록
                    </button>
                  )}
                </div>

                {announcementsLoading ? (
                  <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
                    <div className="animate-pulse p-6">
                      <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    </div>
                  </div>
                ) : announcements.length > 0 ? (
                  <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            제목
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                            등록일
                          </th>
                          {isOwner && (
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                              관리
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {paginatedAnnouncements.map((announcement) => (
                          <tr
                            key={announcement.id}
                            onClick={() => handleSelectAnnouncement(announcement)}
                            className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                              selectedAnnouncement?.id === announcement.id ? 'bg-primary/5' : ''
                            }`}
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                {announcement.is_pinned && (
                                  <span className="text-accent text-sm">📌</span>
                                )}
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900 flex items-center gap-2">
                                    <span>{announcement.title}</span>
                                    {(announcement.comment_count || 0) > 0 && (
                                      <span className="text-sm text-gray-500">
                                        ({announcement.comment_count})
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {new Date(announcement.created_at).toLocaleDateString('ko-KR')}
                            </td>
                            {isOwner && (
                              <td className="px-6 py-4 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditAnnouncement(announcement);
                                    }}
                                    className="p-1 text-primary hover:bg-primary/10 rounded transition-colors"
                                    title="수정"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteAnnouncement(announcement.id);
                                    }}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="삭제"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* 페이지네이션 */}
                    {totalPages > 1 && (
                      <div className="border-t border-gray-200 px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            이전
                          </button>
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`px-3 py-1 rounded-lg border transition-colors ${
                                currentPage === page
                                  ? 'bg-primary text-white border-primary'
                                  : 'border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          ))}
                          <button
                            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            다음
                          </button>
                        </div>
                      </div>
                    )}

                    {/* 선택된 공지사항 상세 보기 */}
                    {selectedAnnouncement && (
                      <div id="announcement-detail" className="border-t border-gray-200 p-6 min-h-[500px]">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {selectedAnnouncement.is_pinned && (
                                <span className="text-accent">📌</span>
                              )}
                              <h3 className="text-lg font-bold text-gray-900">
                                {selectedAnnouncement.title}
                              </h3>
                            </div>
                            <p className="text-sm text-gray-500">
                              {new Date(selectedAnnouncement.created_at).toLocaleString('ko-KR')}
                            </p>
                          </div>
                          <button
                            onClick={() => setSelectedAnnouncement(null)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="닫기"
                          >
                            <X className="w-5 h-5 text-gray-500" />
                          </button>
                        </div>

                        <div
                          className="prose prose-sm max-w-none mb-6 text-gray-700"
                          dangerouslySetInnerHTML={{ __html: selectedAnnouncement.content }}
                        />

                        {/* 댓글 섹션 */}
                        <AnnouncementCommentSection announcementId={selectedAnnouncement.id} />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl shadow-soft p-8 text-center">
                    <p className="text-gray-500">등록된 공지사항이 없습니다.</p>
                    {isOwner && (
                      <button
                        onClick={() => {
                          setShowAnnouncementForm(true);
                          setEditingAnnouncement(null);
                          setAnnouncementTitle('');
                          setAnnouncementContent('');
                          setAnnouncementPinned(false);
                        }}
                        className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm"
                      >
                        <Plus className="w-4 h-4" />
                        첫 공지사항 작성하기
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

            {/* 헤더 + 검색창 */}
            <div id="provider-classes" className="flex items-center justify-between mb-6 scroll-mt-32">
              <h2 className="text-xl font-bold text-gray-900">
                등록된 수업 <span className="text-accent">{filteredClasses.length}</span>개
              </h2>

              {/* 검색 입력 */}
              {classes.length > 0 && (
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="수업명 검색..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
              )}
            </div>

            {/* Class Type Filters */}
            {classes.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap mb-6">
                <button
                  onClick={() => setClassTypeFilter('ALL')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    classTypeFilter === 'ALL'
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  전체
                </button>
                <button
                  onClick={() => setClassTypeFilter('CERTIFICATE')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    classTypeFilter === 'CERTIFICATE'
                      ? 'bg-accent text-white'
                      : 'bg-accent/10 text-accent hover:bg-accent/20'
                  }`}
                >
                  자격증
                </button>
                <button
                  onClick={() => setClassTypeFilter('COMPLETION')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    classTypeFilter === 'COMPLETION'
                      ? 'bg-blue-500 text-white'
                      : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                  }`}
                >
                  수료증
                </button>
                <button
                  onClick={() => setClassTypeFilter('WORKSHOP')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    classTypeFilter === 'WORKSHOP'
                      ? 'bg-purple-500 text-white'
                      : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
                  }`}
                >
                  워크샵
                </button>
                <button
                  onClick={() => setClassTypeFilter('OTHER')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    classTypeFilter === 'OTHER'
                      ? 'bg-gray-400 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  기타
                </button>
              </div>
            )}

            {/* 모집 상태 탭 — 기본은 모집중 */}
            {classes.length > 0 && (
              <div className="flex items-center gap-2 border-b border-gray-200 pb-1 mb-6">
                {([
                  { key: 'RECRUITING', label: '모집중', count: recruitingClasses.length },
                  { key: 'CLOSED', label: '모집 완료', count: closedClasses.length },
                ] as const).map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setClassStatusTab(tab.key)}
                    className={`px-4 py-2 rounded-t-lg text-sm font-semibold transition-colors border-b-2 -mb-[5px] ${
                      classStatusTab === tab.key
                        ? 'border-primary text-primary bg-white'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                    <span className="ml-1.5 text-xs font-bold text-gray-400">{tab.count}</span>
                  </button>
                ))}
              </div>
            )}

            {classesLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : filteredClasses.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {paginatedClasses.map((cls) => (
                    <ClassCard
                      key={cls.id}
                      {...cls}
                      classType={cls.classType || 'OTHER'}
                      onInstructorClick={handleClassInstructorClick}
                    />
                  ))}
                </div>

                {/* 페이지네이션 (6개 = 2열 x 3행 단위) */}
                {classTotalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <button
                      onClick={() => setClassPage((prev) => Math.max(1, prev - 1))}
                      disabled={classPage === 1}
                      className="px-3 py-1 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      이전
                    </button>
                    {Array.from({ length: classTotalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setClassPage(page)}
                        className={`px-3 py-1 rounded-lg border transition-colors ${
                          classPage === page
                            ? 'bg-primary text-white border-primary'
                            : 'border-gray-300 bg-white hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setClassPage((prev) => Math.min(classTotalPages, prev + 1))}
                      disabled={classPage === classTotalPages}
                      className="px-3 py-1 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      다음
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl shadow-soft">
                <p className="text-gray-500">
                  {searchQuery
                    ? `"${searchQuery}"에 대한 검색 결과가 없습니다.`
                    : classes.length > 0
                      ? classStatusTab === 'RECRUITING'
                        ? '현재 모집중인 수업이 없습니다.'
                        : '모집이 완료된 수업이 없습니다.'
                      : '등록된 수업이 없습니다.'
                  }
                </p>
              </div>
            )}
          </div>
        </div>

        {/* 업체 소개 — 그리드 바깥 전체 폭 단독 섹션 */}
        <div id="provider-about" className="mt-8 scroll-mt-32">
          <h2 className="text-xl font-bold text-gray-900 mb-4">업체 소개</h2>
          <div className="bg-white rounded-2xl shadow-soft p-6 sm:p-8">
            {provider.description && provider.description.replace(/<[^>]*>/g, '').trim() ? (
              <div
                className="tiptap prose prose-gray max-w-none"
                dangerouslySetInnerHTML={{ __html: provider.description }}
              />
            ) : (
              <p className="text-sm text-gray-500">업체 소개가 등록되지 않았습니다.</p>
            )}
          </div>
        </div>

        {/* 강사진 섹션 */}
        {(instructorsLoading || instructors.length > 0) && (
          <div id="provider-instructors" className="mt-8 scroll-mt-32">
            <h2 className="text-xl font-bold text-gray-900 mb-4">강사진</h2>

            {instructorsLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl shadow-soft overflow-hidden animate-pulse">
                    <div className="aspect-square bg-gray-200"></div>
                    <div className="p-4">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-full"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                {instructors.map((instructor) => (
                  <InstructorCard
                    key={instructor.id}
                    {...instructor}
                    imageUrl={instructor.image_url}
                    onClick={() => handleInstructorClick(instructor)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* 강사 상세 모달 */}
        <InstructorDetailsModal
          isOpen={showInstructorModal}
          instructor={selectedInstructor}
          onClose={() => {
            setShowInstructorModal(false);
            setSelectedInstructor(null);
          }}
        />
      </div>
    </div>
  );
}
